import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import type { CardView } from "@shared/api";
import { isApiError } from "@web/lib/api";
import { feedApi } from "@web/lib/feedApi";
import { feedKeys, invalidateBoards } from "@web/lib/feedQueries";
import { registerUndo } from "@web/lib/undoRegistry";
import { Button } from "@web/ui/Button";
import { Modal } from "@web/ui/Modal";
import { cancelWork } from "../cardOps";

export function replyThoughtsInstruction(thoughts: string): string {
  return `Write or revise my reply using these thoughts. Put the editable draft on this card with a Send reply button so I can review it; do not send anything. Use my notes naturally, preserve my existing edits when compatible, and do not invent extra facts or promises.\n\nMy thoughts:\n${thoughts.trim()}`;
}
export function ReplyThoughts({
  card,
  beforeQueue,
  disabled,
}: {
  card: CardView;
  beforeQueue: () => Promise<boolean>;
  disabled: boolean;
}) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [thoughts, setThoughts] = useState("");
  const [busy, setBusy] = useState(false);
  async function requestDraft() {
    if (busy || !thoughts.trim()) return;
    setBusy(true);
    try {
      if (!(await beforeQueue())) return;
      const work = await feedApi.instruct(card.feedId, {
        target: { kind: "card", cardId: card.id },
        instruction: replyThoughtsInstruction(thoughts),
      });
      setThoughts("");
      setOpen(false);
      void client.invalidateQueries({ queryKey: feedKeys.card(card.id) });
      invalidateBoards(client);
      void client.invalidateQueries({ queryKey: ["work"] });
      registerUndo({
        message: "Draft requested with your thoughts. It returns to this round when ready.",
        undo: () => cancelWork(client, work.id),
        doneMessage: "Draft request cancelled.",
        tooLateMessage: "Claude has already started preparing the draft.",
      });
    } catch (e) {
      toast(
        isApiError(e) && e.serverMessage
          ? e.serverMessage
          : "Couldn’t request a draft. Your thoughts are still here.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button size="sm" disabled={disabled} onClick={() => setOpen(true)}>
        <MessageSquareText aria-hidden className="size-4" />
        Reply thoughts
      </Button>
      <Modal
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        label="Your thoughts for this reply"
      >
        <form
          className="flex w-[min(520px,90vw)] flex-col gap-3 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            void requestDraft();
          }}
        >
          <h2 className="text-2xl">Your thoughts for this reply</h2>
          <p className="text-sm text-muted">{card.title}</p>
          <label className="flex flex-col gap-1">
            Quick thoughts
            <textarea
              className="rounded-lg border border-line bg-bg p-3"
              rows={5}
              maxLength={4000}
              value={thoughts}
              onChange={(e) => setThoughts(e.target.value)}
              placeholder="Say Tuesday works. Keep it warm and brief. Ask them to send the agenda first."
              disabled={busy}
            />
          </label>
          <p className="text-sm text-muted">
            Rough notes are fine. Claude will draft or revise the reply on this card. Nothing is
            sent until you review and confirm Send reply.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" busy={busy} disabled={!thoughts.trim()}>
              Draft with my thoughts
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
